import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Brain, TrendingUp, TrendingDown, Minus, AlertTriangle,
  CheckCircle, Clock, Target, Lightbulb, BarChart3,
  RefreshCw, Loader2, Calendar, ListChecks, GitBranch,
  ArrowUp, ArrowDown, Activity, Zap, Search
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AIInsights = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [insights, setInsights] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const fetchInsights = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/meeting-intelligence/insights?token=${token}&days=30`);
      setInsights(res.data.insights);
    } catch (err) {
      console.error('Error fetching insights:', err);
      toast.error('Failed to load insights');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  const regenerateInsights = async () => {
    setGenerating(true);
    await fetchInsights();
    setGenerating(false);
    toast.success('Insights regenerated');
  };

  const performSemanticSearch = async () => {
    if (!searchQuery.trim()) return;
    
    try {
      setSearching(true);
      const res = await axios.post(
        `${API}/api/meeting-intelligence/search/semantic?token=${token}`,
        { query: searchQuery, limit: 15 }
      );
      setSearchResults(res.data);
    } catch (err) {
      console.error('Search error:', err);
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'increasing': return <TrendingUp className="h-4 w-4 text-emerald-500" />;
      case 'decreasing': return <TrendingDown className="h-4 w-4 text-red-500" />;
      default: return <Minus className="h-4 w-4 text-slate-400" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700 border-red-200';
      case 'medium': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'low': return 'bg-blue-100 text-blue-700 border-blue-200';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'high': return 'text-red-600 bg-red-50';
      case 'medium': return 'text-amber-600 bg-amber-50';
      case 'low': return 'text-emerald-600 bg-emerald-50';
      default: return 'text-slate-600 bg-slate-50';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Brain className="h-12 w-12 text-purple-600 mx-auto mb-4 animate-pulse" />
          <p className="text-slate-500">Analyzing your meeting data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="ai-insights">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
            <Brain className="h-7 w-7 text-purple-600" />
            AI Insights
          </h1>
          <p className="text-slate-500 mt-1">AI-powered analytics and recommendations</p>
        </div>
        <Button onClick={regenerateInsights} disabled={generating}>
          {generating ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Regenerate
        </Button>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="recommendations" className="flex items-center gap-2">
            <Lightbulb className="h-4 w-4" />
            Recommendations
          </TabsTrigger>
          <TabsTrigger value="predictions" className="flex items-center gap-2">
            <Zap className="h-4 w-4" />
            Predictions
          </TabsTrigger>
          <TabsTrigger value="search" className="flex items-center gap-2">
            <Search className="h-4 w-4" />
            Smart Search
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          {insights && (
            <div className="space-y-6">
              {/* Key Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="border-l-4 border-l-blue-500">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Total Meetings</p>
                        <p className="text-2xl font-bold">{insights.meeting_metrics?.total || 0}</p>
                        <div className="flex items-center gap-1 mt-1">
                          {getTrendIcon(insights.trends?.meetings_trend)}
                          <span className="text-xs text-slate-500 capitalize">{insights.trends?.meetings_trend}</span>
                        </div>
                      </div>
                      <Calendar className="h-8 w-8 text-blue-500 opacity-50" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-amber-500">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Task Completion</p>
                        <p className="text-2xl font-bold">{insights.task_metrics?.completion_rate || 0}%</p>
                        <div className="flex items-center gap-1 mt-1">
                          {getTrendIcon(insights.trends?.tasks_trend)}
                          <span className="text-xs text-slate-500 capitalize">{insights.trends?.tasks_trend}</span>
                        </div>
                      </div>
                      <ListChecks className="h-8 w-8 text-amber-500 opacity-50" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-emerald-500">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Decisions Made</p>
                        <p className="text-2xl font-bold">{insights.decision_metrics?.total || 0}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {insights.decision_metrics?.decisions_per_meeting || 0} per meeting
                        </p>
                      </div>
                      <GitBranch className="h-8 w-8 text-emerald-500 opacity-50" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-red-500">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Overdue Tasks</p>
                        <p className="text-2xl font-bold">{insights.task_metrics?.overdue_count || 0}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          Needs attention
                        </p>
                      </div>
                      <AlertTriangle className="h-8 w-8 text-red-500 opacity-50" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Trends Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-purple-600" />
                    Weekly Trends (Last 4 Weeks)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {['Meetings', 'Tasks Created', 'Tasks Completed'].map((label, idx) => {
                      const data = idx === 0 
                        ? insights.trends?.weekly_data?.meetings 
                        : idx === 1 
                          ? insights.trends?.weekly_data?.tasks 
                          : insights.trends?.weekly_data?.completed;
                      const maxVal = Math.max(...(data || [1]));
                      
                      return (
                        <div key={label} className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600">{label}</span>
                            <span className="font-medium">{(data || []).reduce((a, b) => a + b, 0)} total</span>
                          </div>
                          <div className="flex gap-1 h-8">
                            {(data || [0, 0, 0, 0]).map((val, i) => (
                              <div
                                key={i}
                                className={`flex-1 rounded ${
                                  idx === 0 ? 'bg-blue-500' : idx === 1 ? 'bg-amber-500' : 'bg-emerald-500'
                                }`}
                                style={{ opacity: maxVal > 0 ? 0.3 + (val / maxVal) * 0.7 : 0.3 }}
                                title={`Week ${4 - i}: ${val}`}
                              />
                            ))}
                          </div>
                          <div className="flex justify-between text-xs text-slate-400">
                            <span>4 weeks ago</span>
                            <span>This week</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Meeting Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Meeting Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(insights.meeting_metrics?.by_type || {}).map(([type, count]) => (
                        <div key={type} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${
                              type === 'teams' ? 'bg-purple-500' : 
                              type === 'hybrid' ? 'bg-amber-500' : 'bg-blue-500'
                            }`} />
                            <span className="capitalize">{type}</span>
                          </div>
                          <span className="font-medium">{count}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 pt-4 border-t">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Avg. Duration</span>
                        <span className="font-medium">{insights.meeting_metrics?.avg_duration_minutes || 0} min</span>
                      </div>
                      <div className="flex justify-between text-sm mt-2">
                        <span className="text-slate-500">Busiest Day</span>
                        <span className="font-medium">{insights.meeting_metrics?.busiest_day || 'N/A'}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Task Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(insights.task_metrics?.by_status || {}).map(([status, count]) => (
                        <div key={status} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${
                              status === 'completed' ? 'bg-emerald-500' :
                              status === 'in_progress' ? 'bg-blue-500' :
                              status === 'todo' ? 'bg-slate-400' : 'bg-purple-500'
                            }`} />
                            <span className="capitalize">{status.replace('_', ' ')}</span>
                          </div>
                          <span className="font-medium">{count}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 pt-4 border-t">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Avg. Completion Time</span>
                        <span className="font-medium">{insights.task_metrics?.avg_completion_days || 0} days</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Recommendations Tab */}
        <TabsContent value="recommendations">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-amber-500" />
                AI Recommendations
              </CardTitle>
              <CardDescription>Actionable insights to improve your meeting effectiveness</CardDescription>
            </CardHeader>
            <CardContent>
              {insights?.recommendations?.length > 0 ? (
                <div className="space-y-4">
                  {insights.recommendations.map((rec, idx) => (
                    <div key={idx} className={`p-4 rounded-lg border ${getPriorityColor(rec.priority)}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={getPriorityColor(rec.priority)}>
                            {rec.priority}
                          </Badge>
                          <Badge variant="outline" className="capitalize">
                            {rec.category}
                          </Badge>
                        </div>
                      </div>
                      <h4 className="font-medium mt-2">{rec.title}</h4>
                      <p className="text-sm mt-1 opacity-80">{rec.description}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Lightbulb className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No recommendations available yet.</p>
                  <p className="text-sm mt-1">Generate more meeting data to get AI recommendations.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Predictions Tab */}
        <TabsContent value="predictions">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Zap className="h-5 w-5 text-purple-600" />
                  Workload Predictions
                </CardTitle>
              </CardHeader>
              <CardContent>
                {insights?.predictions ? (
                  <div className="space-y-4">
                    <div className={`p-4 rounded-lg ${
                      insights.predictions.workload_level === 'high' ? 'bg-red-50' :
                      insights.predictions.workload_level === 'medium' ? 'bg-amber-50' : 'bg-emerald-50'
                    }`}>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Current Workload</span>
                        <Badge className={`capitalize ${
                          insights.predictions.workload_level === 'high' ? 'bg-red-500' :
                          insights.predictions.workload_level === 'medium' ? 'bg-amber-500' : 'bg-emerald-500'
                        }`}>
                          {insights.predictions.workload_level}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600 mt-2">
                        {insights.predictions.pending_tasks} pending tasks ({insights.predictions.high_priority_pending} high priority)
                      </p>
                    </div>

                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-600">Estimated Clear Date</span>
                        <span className="font-medium">
                          {insights.predictions.estimated_clear_date 
                            ? new Date(insights.predictions.estimated_clear_date).toLocaleDateString()
                            : 'N/A'
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-slate-500">No predictions available</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  Risk Assessment
                </CardTitle>
              </CardHeader>
              <CardContent>
                {insights?.predictions?.risk_assessment ? (
                  <div className="space-y-4">
                    <div className={`p-4 rounded-lg ${getRiskColor(insights.predictions.risk_assessment.overdue_risk)}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Overdue Risk</span>
                        <Badge className={`capitalize ${
                          insights.predictions.risk_assessment.overdue_risk === 'high' ? 'bg-red-500' :
                          insights.predictions.risk_assessment.overdue_risk === 'medium' ? 'bg-amber-500' : 'bg-emerald-500'
                        }`}>
                          {insights.predictions.risk_assessment.overdue_risk}
                        </Badge>
                      </div>
                    </div>

                    <div className={`p-4 rounded-lg ${getRiskColor(insights.predictions.risk_assessment.workload_risk)}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Workload Risk</span>
                        <Badge className={`capitalize ${
                          insights.predictions.risk_assessment.workload_risk === 'high' ? 'bg-red-500' :
                          insights.predictions.risk_assessment.workload_risk === 'medium' ? 'bg-amber-500' : 'bg-emerald-500'
                        }`}>
                          {insights.predictions.risk_assessment.workload_risk}
                        </Badge>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-slate-500">No risk data available</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Smart Search Tab */}
        <TabsContent value="search">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Search className="h-5 w-5 text-blue-600" />
                AI-Powered Semantic Search
              </CardTitle>
              <CardDescription>Search across all meetings, tasks, decisions, and transcripts</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-6">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && performSemanticSearch()}
                  placeholder="Search for anything... (e.g., 'budget discussions', 'Q4 planning')"
                  className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <Button onClick={performSemanticSearch} disabled={searching}>
                  {searching ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                </Button>
              </div>

              {searchResults && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between text-sm text-slate-500">
                    <span>{searchResults.total} results for "{searchResults.query}"</span>
                    <Badge variant="outline">{searchResults.search_type} search</Badge>
                  </div>

                  {searchResults.results?.length > 0 ? (
                    <div className="space-y-3">
                      {searchResults.results.map((result, idx) => (
                        <div key={idx} className="p-4 border rounded-lg hover:bg-slate-50 transition-colors">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="capitalize">{result.type}</Badge>
                              {result.status && (
                                <Badge variant="secondary" className="capitalize">{result.status}</Badge>
                              )}
                            </div>
                            <span className="text-xs text-slate-400">
                              {Math.round((result.relevance_score || 0) * 100)}% match
                            </span>
                          </div>
                          <h4 className="font-medium mt-2">{result.title}</h4>
                          {result.content && (
                            <p className="text-sm text-slate-600 mt-1 line-clamp-2">{result.content}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <Search className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No results found</p>
                    </div>
                  )}
                </div>
              )}

              {!searchResults && (
                <div className="text-center py-12 text-slate-500">
                  <Brain className="h-16 w-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg font-medium">AI-Powered Search</p>
                  <p className="text-sm mt-1">Enter a query to search across all your meeting data</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AIInsights;
